/**
 * リトライ対象のHTTPステータスコード
 */
const RETRYABLE_STATUS_CODES = [502, 503, 504, 429] as const;

const MAX_RETRIES = 3;
const TIMEOUT_MS = 60000;
const RETRY_DELAYS_MS = [2000, 4000, 8000];

export interface RetryConfig {
  enabled: boolean;
  /**
   * 応答を待つ上限(ミリ秒)。省略時は TIMEOUT_MS。
   * 復元処理など画面全体をブロックする呼び出しは、これを短くして早く決着させる。
   */
  timeoutMs?: number;
}

function isRetryableStatus(status: number): boolean {
  return (RETRYABLE_STATUS_CODES as readonly number[]).includes(status);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * リトライ可能なHTTPリクエストを実行する
 *
 * @param fetchFn リクエストを実行する関数
 * @param config リトライ設定
 * @returns レスポンス
 * @throws タイムアウトまたは全てのリトライ失敗時にエラー
 */
export async function fetchWithRetry(
  fetchFn: () => Promise<Response>,
  config: RetryConfig,
): Promise<Response> {
  const timeoutMs = config.timeoutMs ?? TIMEOUT_MS;

  // タイムアウト処理を設定
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`Request timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    // リトライが無効な場合は、タイムアウト付きで1回だけ実行
    if (!config.enabled) {
      return await Promise.race([fetchFn(), timeoutPromise]);
    }

    // リトライ処理
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await Promise.race([fetchFn(), timeoutPromise]);

        // レスポンスが成功またはリトライ対象外のエラーの場合は返す
        if (response.ok || !isRetryableStatus(response.status)) {
          return response;
        }

        // 最後の試行の場合はそのまま返す
        if (attempt === MAX_RETRIES) {
          return response;
        }

        // リトライ対象のエラーの場合は待機してリトライ
        await delay(RETRY_DELAYS_MS[attempt]);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // 最後の試行の場合はエラーを投げる
        if (attempt === MAX_RETRIES) {
          throw lastError;
        }

        // ネットワークエラーの場合もリトライ
        await delay(RETRY_DELAYS_MS[attempt]);
      }
    }

    throw new Error('Unexpected error in fetchWithRetry');
  } finally {
    // 決着後もタイマーが残ると、上限ぶんの間プロセスに未解決のタイマーが残り続ける
    clearTimeout(timeoutId);
  }
}
